import { useState } from 'react';
import type { CheckResult, DnsRecord, Domain } from '../types';

/*
  DNS YAZILARININ GÖSTƏRİLMƏSİ — TƏK MƏNBƏ.

  ⚠️ BU KOD ƏVVƏL YALNIZ `Onboarding.tsx` İÇİNDƏ İDİ VƏ REAL PROBLEM YARATDI.
  Qeydiyyatdan keçən müştəri qarşılama ekranını bir dəfə keçir, sonra ona
  bir daha qayıda bilmir: domen aktivləşəndən sonra o ekran ümumiyyətlə
  başqa mərhələ göstərir. Nəticədə «TXT, MX yazılarını harda görüm?»
  sualı bizə düşür — halbuki məlumat sistemdə var, sadəcə görünmür.

  İndi eyni komponent həm qarşılama ekranında, həm də Ayarlar bölməsində
  işlədilir. Nüsxə çıxarılsaydı, iki nüsxə vaxtla ayrılardı: birində
  düzəldilən mətn digərində köhnə qalar və müştəri iki yerdə İKİ FƏRQLİ
  təlimat görərdi — DNS-də bu, birbaşa poçtun dayanması deməkdir.
*/

/** DNS dəyərini panoya kopyalayan kiçik düymə. */
export function Copy({ text }: { text: string }): React.ReactElement {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn-copy"
      title="Kopyala"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? '✓' : '⧉'}
    </button>
  );
}

/** Bir yoxlamanın nəticəsi — uğursuzdursa NƏYİN tapıldığını da göstərir. */
export function Check({ label, r }: { label: string; r: CheckResult }): React.ReactElement {
  return (
    <li className={r.ok ? 'chk is-ok' : 'chk is-bad'}>
      <span className="chk-mark">{r.ok ? '✓' : '✗'}</span>
      <div className="chk-body">
        <strong>{label}</strong>
        {r.found.length > 0 ? (
          <code className="chk-found">{r.found.join(' · ')}</code>
        ) : (
          <span className="chk-none">DNS-də tapılmadı</span>
        )}
        {!r.ok && r.hint ? <p className="chk-hint">{r.hint}</p> : null}
      </div>
    </li>
  );
}

/** DNS bayraqları — hansı yoxlamanın keçdiyi bir baxışda görünsün. */
export function DnsFlags({ d }: { d: Domain }): React.ReactElement {
  const flags: Array<[string, boolean]> = [
    ['MX', d.mxOk],
    ['SPF', d.spfOk],
    ['DKIM', d.dkimOk],
    ['DMARC', d.dmarcOk],
  ];
  return (
    <span className="dns-flags">
      {flags.map(([label, ok]) => (
        <span key={label} className={ok ? 'flag is-ok' : 'flag is-bad'} title={ok ? 'Keçdi' : 'Keçmədi'}>
          {label}
        </span>
      ))}
    </span>
  );
}

/**
 * DNS yazılarının qrupları.
 *
 * ⚠️ SIRA SABİTDİR VƏ MƏNALIDIR — «vaciblikdən azalan» ardıcıllıq.
 * Birinci qrup həmişə açıq göstərilir, qalanları yığılmış: müştəri
 * poçtunu işə salmaq üçün NƏYİN mütləq lazım olduğunu dərhal görməlidir.
 */
export const GROUPS = [
  {
    key: 'activation' as const,
    title: 'Poçtun gəlməsi üçün',
    lead: 'Bu iki yazı olmadan domeninizə məktub GƏLMİR. Əvvəlcə bunları qurun.',
  },
  {
    key: 'delivery' as const,
    title: 'Spama düşməmək üçün',
    lead:
      'Bunlarsız poçt gəlir, amma SİZİN göndərdiyiniz məktublar qarşı tərəfdə ' +
      'spama düşür. Sonra da əlavə edə bilərsiniz — nə qədər tez, o qədər yaxşı.',
  },
  {
    key: 'clients' as const,
    title: 'Outlook və telefonlar üçün',
    /*
      ⚠️ MƏTN DƏYİŞDİRİLDİ (2026-08-09) — ƏVVƏLKİ YANLIŞ İDİ.
      Orada «bunlarsız hər şey işləyir, sadəcə əl ilə yazmaq lazım gəlir»
      yazılırdı. Real müştəridə əksi oldu: bu qeydləri olan domen
      Outlook-a əlavə olundu, olmayan İKİ domen isə ÜMUMİYYƏTLƏ əlavə
      olunmadı — Outlook serveri tapa bilmədiyi üçün hesabı qəbul
      etmədi. Poçt jurnalında həmin qutular üçün xaricdən bir dənə də
      giriş cəhdi görünmür.

      Müasir Outlook (Microsoft buludundan keçən variant) əl ilə
      quraşdırmanı həmişə təklif etmir. Ona görə mətn «rahatlıq» kimi
      yox, «Outlook üçün şərt» kimi yazılır.
    */
    lead:
      'Poçt bunlarsız da gəlir və gedir. Amma müasir Outlook hesabı ' +
      'ümumiyyətlə əlavə etməkdən imtina edə bilər — server ünvanını ' +
      'tapa bilmir. Əlavə etsəniz, istifadəçi yalnız ünvan və şifrə yazır.',
  },
];

/** Bir DNS yazısının kartı — registrar formasının sahələrinə uyğun. */
export function DnsCard({ r }: { r: DnsRecord }): React.ReactElement {
  return (
    <div className={`dns-card${r.required ? '' : ' is-optional'}`}>
      <div className="dns-head">
        <span className="dns-type">{r.type}</span>
        <span className="dns-req">{r.required ? 'məcburi' : 'məcburi deyil'}</span>
      </div>

      <dl className="dns-fields">
        <dt>
          Ad <span className="dns-en">Name / Host</span>
        </dt>
        <dd>
          <code>{r.name}</code>
          <Copy text={r.name} />
          {/*
            ⚠️ «@» ALTERNATİVİ GÖSTƏRİLMƏLİDİR.
            GoDaddy, Namecheap, cPanel və əksər yerli registrar kök domen
            üçün «@» gözləyir. Müştəri tam domeni yazanda registrar onu
            domenin ARXASINA əlavə edir və `sirket.az.sirket.az` alınır.
            Xəta görünmür, sadəcə poçt gəlmir — tapılması ən çətin
            nasazlıqlardandır. Yandex bu ipucunu açıq verir.
          */}
          {r.nameAlt && (
            <span className="dns-alt">
              və ya <code>{r.nameAlt}</code> (yaxud boş buraxın)
            </span>
          )}
        </dd>

        {r.priority !== undefined ? (
          <>
            <dt>
              Prioritet <span className="dns-en">Priority</span>
            </dt>
            <dd>
              <code>{r.priority}</code>
              <Copy text={String(r.priority)} />
            </dd>
          </>
        ) : null}

        {/*
          SRV-də dörd ayrı sahə var. Registrar formaları da onları
          ayrı istəyir, ona görə burada da ayrı göstərilir — «1 993
          imap.mailora.az» mətnini bütöv yapışdırmaq qeydi səssizcə
          sındırır.
        */}
        {r.type === 'SRV' && r.target ? (
          <>
            <dt>
              Çəki <span className="dns-en">Weight</span>
            </dt>
            <dd>
              <code>{r.weight}</code>
              <Copy text={String(r.weight)} />
            </dd>
            <dt>
              Port <span className="dns-en">Port</span>
            </dt>
            <dd>
              <code>{r.port}</code>
              <Copy text={String(r.port)} />
            </dd>
            <dt>
              Hədəf <span className="dns-en">Target</span>
            </dt>
            <dd>
              <code>{r.target}</code>
              <Copy text={r.target} />
            </dd>
          </>
        ) : (
          <>
            <dt>
              Dəyər <span className="dns-en">Content / Value</span>
            </dt>
            <dd>
              <code>{r.value}</code>
              <Copy text={r.value} />
            </dd>
          </>
        )}
      </dl>

      {/*
        ⚠️ ƏVƏZETMƏ XƏBƏRDARLIĞI AYRICA VƏ GÖZƏ ÇARPAN.
        Bunu `note` mətninin içində yazsaq, uzun izahın ortasında itir.
        Halbuki bu, ən zərərli səhvin qarşısını alır: müştəri bizim MX-i
        köhnəsinin YANINA qoyur, prioriteti kiçik olan qazanır və poçt
        köhnə sistemə getməyə davam edir — müştəri isə hər şeyi düzgün
        etdiyini düşünüb «poçt gəlmir» deyə şikayət edir.
      */}
      {r.replacesExisting && (
        <p className="dns-replace">
          <strong>Əvvəlcə mövcud {r.type} yazılarını silin.</strong> Bu yazı onların
          yanına yox, ƏVƏZİNƏ qoyulmalıdır.
        </p>
      )}
      <p className="dns-note">{r.note}</p>
    </div>
  );
}

/**
 * Bütün yazılar — üç qrupda.
 *
 * ⚠️ ÜÇ QRUP, BİRİ AÇIQ — QALANLARI YIĞILMIŞ.
 * Əvvəl 10 yazı bir siyahıda verilirdi. Registrar panelində 10 yazı quran
 * adam ya yarıda qalır, ya da səhv qurur. Rəqiblərin heç biri hamısını
 * birdən istəmir: Titan MX + bir TXT ilə aktivləşir, Zoho SPF/DKIM-i
 * domen işə düşəndən SONRA verir.
 *
 * Qruplar `group` sahəsindən gəlir və sıra SABİTDİR — ilk qrup həmişə
 * açıq, çünki onsuz poçt ümumiyyətlə gəlmir.
 */
export function DnsGroups({ records }: { records: DnsRecord[] }): React.ReactElement {
  return (
    <>
      {GROUPS.map(({ key, title, lead }, gi) => {
        const rows = records.filter((r) => r.group === key);
        if (rows.length === 0) return null;
        const body = (
          <div className="dns-table">
            {rows.map((r, i) => (
              <DnsCard key={`${key}-${i}`} r={r} />
            ))}
          </div>
        );
        // İlk qrup <details> içində DEYİL: məcburi addımı gizlətmək olmaz.
        return gi === 0 ? (
          <section key={key} className="dns-group">
            <h3 className="dns-group-title">{title}</h3>
            <p className="dns-group-lead">{lead}</p>
            {body}
          </section>
        ) : (
          <details key={key} className="dns-group">
            <summary>
              <span className="dns-group-title">{title}</span>
              <span className="dns-group-count">{rows.length} yazı</span>
            </summary>
            <p className="dns-group-lead">{lead}</p>
            {body}
          </details>
        );
      })}
    </>
  );
}
